import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Eye, Phone, Globe, Mail, MapPin, Heart, Percent, Calendar, Rocket, TrendingUp, Bell, BellRing, Send, Smartphone } from 'lucide-react';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: 'all', label: 'All Time' },
];

export const AnalyticsSection = ({ analytics, analyticsPeriod, changeAnalyticsPeriod }) => {
  const { t } = useTranslation();
  if (!analytics) return null;

  const eng = analytics.totalEngagement || 0;
  const isLow = eng < 10;
  const isHigh = eng >= 30;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="analytics-card">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            {t('performance', 'Performance')}
          </h2>
        </div>
        {/* Period filter */}
        <div className="flex gap-1.5 mt-3" data-testid="analytics-period-filter">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => changeAnalyticsPeriod(p.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                analyticsPeriod === p.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`analytics-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main metrics */}
      <div className="p-4">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
          <MetricCard icon={Eye} value={analytics.views} label={t('analyticsViews', 'Views')} color="purple" testId="stat-views" />
          <MetricCard icon={Phone} value={analytics.call} label={t('calls', 'Calls')} color="green" testId="stat-calls" />
          <MetricCard icon={Globe} value={analytics.website} label={t('website', 'Website')} color="blue" testId="stat-website" />
          <MetricCard icon={Mail} value={analytics.email} label={t('emailButton', 'Email')} color="amber" testId="stat-email" />
          <MetricCard icon={MapPin} value={analytics.directions} label={t('directions', 'Directions')} color="indigo" testId="stat-directions" />
          <MetricCard icon={Heart} value={analytics.favourites} label={t('favourited', 'Saved')} color="red" testId="stat-favourites" />
          <MetricCard icon={Percent} value={analytics.offerViews} label={t('offerViews', 'Offer Views')} color="orange" testId="stat-offer-views" />
          <MetricCard icon={Calendar} value={analytics.eventViews} label={t('eventViews', 'Event Views')} color="teal" testId="stat-event-views" />
        </div>

        {/* Audience reach — private to the business owner */}
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="h-4 w-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-gray-700">
              {t('analyticsAudience', 'Audience reach')}
            </h3>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-gray-400">
              {t('analyticsPrivate', 'Only you can see this')}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="analytics-audience-row">
            <MetricCard icon={BellRing} value={analytics.followers} label={t('followersLabel', 'Followers')} color="purple" testId="stat-followers" />
            <MetricCard icon={Bell} value={analytics.notificationCapableDevices} label={t('canReceiveAlertsLabel', 'Can receive alerts')} color="indigo" testId="stat-notif-capable" />
            <MetricCard icon={Smartphone} value={analytics.webPushSubscribers} label={t('webPushLabel', 'Web push enrolled')} color="emerald" testId="stat-webpush-subs" />
            <MetricCard icon={Send} value={analytics.notificationsDelivered30d} label={t('alertsDelivered30dLabel', 'Alerts sent (30d)')} color="blue" testId="stat-alerts-30d" />
          </div>
        </div>

        {/* Boost status */}
        {analytics.boostActive && (
          <div className="mt-4 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl flex items-center gap-3" data-testid="boost-active-banner">
            <div className="p-2 bg-amber-400 rounded-lg">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">{t('boostStatusActive', 'Boost Active')}</p>
              <p className="text-xs text-amber-600">{analytics.boostViewsDuring} {t('viewsDuringBoost', 'views during boost')}</p>
            </div>
          </div>
        )}

        {/* Upsell messaging */}
        {!analytics.boostActive && (
          <div className={`mt-4 px-4 py-3 rounded-xl flex items-center gap-3 ${
            isHigh ? 'bg-green-50 border border-green-200' : 'bg-purple-50 border border-purple-200'
          }`} data-testid="analytics-upsell">
            <TrendingUp className={`h-5 w-5 flex-shrink-0 ${isHigh ? 'text-green-600' : 'text-purple-600'}`} />
            <p className={`text-sm ${isHigh ? 'text-green-700' : 'text-purple-700'}`}>
              {isHigh
                ? t('upsellHigh', "You're getting attention — boost to reach even more people")
                : isLow
                  ? t('upsellLow', 'Boost your visibility to reach more people')
                  : t('upsellMedium', 'Increase your reach with a boost')
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const COLORS = {
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-500' },
  green: { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-500' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-500' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-500' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'text-indigo-500' },
  red: { bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-500' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-500' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-700', icon: 'text-teal-500' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-500' },
};

const MetricCard = ({ icon: Icon, value, label, color, testId }) => {
  const c = COLORS[color] || COLORS.purple;
  return (
    <div className={`${c.bg} p-3 rounded-lg text-center`} data-testid={testId}>
      <Icon className={`h-4 w-4 ${c.icon} mx-auto mb-1`} />
      <p className={`text-xl font-bold ${c.text}`}>{value ?? 0}</p>
      <p className={`text-[10px] ${c.icon} mt-0.5`}>{label}</p>
    </div>
  );
};
